import PocketBase, {type AuthModel} from 'pocketbase';
import type {TPBConfigCollection} from '~/types';

type TPBAuthStore = {
  token: string
  record: AuthModel
}

const PERMISSIONS = ['xray', 'shadowsocks', 'wireguard', 'minecraft', 'invite'];

export default defineNuxtPlugin(async () => {
  clearOldCookie();

  const domainConfig = useDomainConfig();
  const pb = new PocketBase(domainConfig.apiBase);

  const cookie = useCookie<TPBAuthStore>('pb_auth', {
    path:     '/',
    secure:   true,
    sameSite: 'strict',
    httpOnly: false,
    maxAge:   604800,
    domain:   import.meta.dev ? undefined : `.${domainConfig.domain}`
  });

  pb.authStore.save(cookie.value?.token, cookie.value?.record);

  pb.authStore.onChange(() => {
    cookie.value = {
      token: pb.authStore.token,
      record: pb.authStore.record,
    };
  });

  try {
    if (pb.authStore.isValid) await pb.collection('users').authRefresh();
  } catch (_) {
    pb.authStore.clear();
  }

  addRouteMiddleware('only-authed', (_to, _from) => {
    if (!pb.authStore.isValid) {
      return navigateTo('/login');
    }
  });

  addRouteMiddleware('only-unauthed', (_to, _from) => {
    if (pb.authStore.isValid) {
      return navigateTo('/');
    }
  });

  PERMISSIONS.forEach(perm => {
    addRouteMiddleware(`only-with-${perm}`, (_to, _from) => {
      try {
        if (!hasPermission(perm)) {
          return navigateTo('/');
        }
      } catch (_) {
        return navigateTo('/login');
      }
    });
  });

  const [pbConf] = (await pb.collection<TPBConfigCollection>('config').getList(0, 1)).items;
  if (!pbConf) {
    throw new Error('No config document found');
  }

  const hasPermission = (perm: string) => {
    if (!pb.authStore.isValid) return false;
    return pb.authStore.record!.permissions.includes(perm);
  };

  return {
    provide: {
      pb,
      pbConf,
      hasPermission
    }
  };
});

function clearOldCookie() {
  if (import.meta.client && !import.meta.dev)
    document.cookie = 'pb_auth=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; secure; samesite=strict';
};
